import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Cookie, Shield, BarChart3, Target, Settings } from 'lucide-react';
import {
  CookiePreferences,
  DEFAULT_PREFERENCES,
  OPEN_PREFERENCES_EVENT,
  getStoredConsent,
  saveConsent,
  applyGoogleConsentMode,
} from '@/lib/cookie-consent';

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPreferences(stored.preferences);
      applyGoogleConsentMode(stored.preferences);
      return;
    }
    // No decision yet — surface the banner after a short delay so the
    // first paint isn't interrupted.
    const timer = window.setTimeout(() => setVisible(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const reopen = () => {
      const stored = getStoredConsent();
      if (stored) setPreferences(stored.preferences);
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, reopen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, reopen);
  }, []);

  const commit = (prefs: CookiePreferences) => {
    saveConsent(prefs);
    setPreferences(prefs);
    setShowDetails(false);
    setVisible(false);
  };

  const acceptAll = () =>
    commit({ necessary: true, analytics: true, marketing: true, functional: true });

  const rejectNonEssential = () => commit(DEFAULT_PREFERENCES);

  const saveChoices = () => commit(preferences);

  const toggle = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-heading"
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-border bg-background/95 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 hidden sm:block">
            <Cookie className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2 id="cookie-consent-heading" className="text-base font-semibold text-foreground">
              {t('cookie.heading')}
            </h2>
            <p className="mt-1 text-sm text-foreground/80 leading-relaxed">
              {t('cookie.body')} {t('cookie.readPolicy')}{' '}
              <a href="/cookie-policy" className="text-primary underline underline-offset-4 hover:no-underline">
                {t('cookie.cookiePolicyLink')}
              </a>{' '}
              {t('cookie.andLink')}{' '}
              <a href="/privacy-policy" className="text-primary underline underline-offset-4 hover:no-underline">
                {t('cookie.privacyPolicyLink')}
              </a>
              .
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3 rounded-xl border border-border bg-muted p-4">
                <Row
                  title={t('cookie.categories.necessary.title')}
                  description={t('cookie.categories.necessary.body')}
                  icon={<Shield className="h-4 w-4 text-emerald-400" aria-hidden="true" />}
                  checked
                  disabled
                  badge={t('cookie.categories.necessary.badge')}
                />
                <Row
                  title={t('cookie.categories.analytics.title')}
                  description={t('cookie.categories.analytics.body')}
                  icon={<BarChart3 className="h-4 w-4 text-sky-400" aria-hidden="true" />}
                  checked={preferences.analytics}
                  onChange={(v) => toggle('analytics', v)}
                />
                <Row
                  title={t('cookie.categories.marketing.title')}
                  description={t('cookie.categories.marketing.body')}
                  icon={<Target className="h-4 w-4 text-fuchsia-400" aria-hidden="true" />}
                  checked={preferences.marketing}
                  onChange={(v) => toggle('marketing', v)}
                />
                <Row
                  title={t('cookie.categories.functional.title')}
                  description={t('cookie.categories.functional.body')}
                  icon={<Settings className="h-4 w-4 text-amber-400" aria-hidden="true" />}
                  checked={preferences.functional}
                  onChange={(v) => toggle('functional', v)}
                />
              </div>
            )}

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Button
                variant="outline"
                onClick={() => setShowDetails((v) => !v)}
                className="sm:flex-none border-border bg-muted text-foreground hover:bg-muted"
              >
                {showDetails ? t('cookie.buttons.hideDetails') : t('cookie.buttons.customize')}
              </Button>
              <Button
                variant="outline"
                onClick={rejectNonEssential}
                className="sm:flex-1 border-border bg-muted text-foreground hover:bg-muted"
              >
                {t('cookie.buttons.reject')}
              </Button>
              {showDetails && (
                <Button
                  variant="outline"
                  onClick={saveChoices}
                  className="sm:flex-1 border-primary/40 bg-primary/10 text-foreground hover:bg-primary/20"
                >
                  {t('cookie.buttons.save')}
                </Button>
              )}
              <Button
                onClick={acceptAll}
                className="sm:flex-1 btn-primary"
              >
                {t('cookie.buttons.acceptAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RowProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  badge?: string;
  onChange?: (value: boolean) => void;
}

const Row = ({ title, description, icon, checked, disabled, badge, onChange }: RowProps) => (
  <div className="flex items-start gap-3">
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={(v) => onChange?.(Boolean(v))}
      aria-label={title}
      className="mt-0.5"
    />
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2">
        {icon}
        <span className="text-sm font-medium text-foreground">{title}</span>
        {badge && (
          <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-400/30">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-foreground/70 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default CookieConsent;
